import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Play, ArrowLeft, Gamepad2, Target, Star, Zap, Users } from 'lucide-react'

export default function SimpleMatchInfoPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-dark-navy/70 hover:text-dark-navy mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Games
      </Link>

      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">🐚</div>
        <h1 className="text-4xl md:text-5xl font-bold text-dark-navy mb-4">Simple Match</h1>
        <p className="text-xl text-dark-navy/70 max-w-2xl mx-auto">
          A classic memory matching game with an ocean twist! Flip shells to find matching pairs 
          of tropical sea creatures.
        </p>
        
        {/* Play Button */}
        <div className="mt-8">
          <Link to="/simple-match/play">
            <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white">
              <Play className="w-5 h-5 mr-2" />
              Start Playing
            </Button>
          </Link>
        </div>
      </div>

      {/* Screenshot Section */}
      <div className="mb-12">
        <div className="bg-gradient-to-br from-pale-aqua/80 to-medium-teal/60 rounded-2xl overflow-hidden shadow-xl aspect-video flex items-center justify-center">
          <img 
            src="/screenshots/simple_match.jpg" 
            alt="Simple Match gameplay screenshot"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <div className="hidden text-center p-8">
            <Gamepad2 className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <p className="text-white/70">Screenshot coming soon!</p>
          </div>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Target className="w-6 h-6 text-medium-teal" />
          How to Play
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">👆</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Click to Flip</h3>
              <p className="text-dark-navy/70 text-sm">Click or tap on any shell to reveal the sea creature hiding underneath</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🔍</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Find Pairs</h3>
              <p className="text-dark-navy/70 text-sm">Flip two shells at a time and try to find matching creatures</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🧠</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Remember Locations</h3>
              <p className="text-dark-navy/70 text-sm">Use your memory to remember where each creature is hiding</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🏆</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Match All Pairs</h3>
              <p className="text-dark-navy/70 text-sm">Find all the matching pairs to complete the level!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Game Modes */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Users className="w-6 h-6 text-medium-purple" />
          Game Modes
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 bg-gradient-to-br from-medium-teal/10 to-light-teal/20 rounded-xl border-2 border-medium-teal/30">
            <h3 className="font-bold text-dark-navy text-lg mb-2">🎮 Practice Mode</h3>
            <p className="text-dark-navy/70">
              Play solo and try to match all pairs in as few moves as possible. 
              Earn pearls for every match!
            </p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-medium-purple/10 to-deep-purple/20 rounded-xl border-2 border-medium-purple/30">
            <h3 className="font-bold text-dark-navy text-lg mb-2">👫 2 Player Mode</h3>
            <p className="text-dark-navy/70">
              Take turns with a friend! The player who finds the most pairs wins. 
              Perfect for family game time!
            </p>
          </div>
        </div>
      </div>

      {/* Tips & Streak System */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-bright-coral" />
          Streak Bonus System
        </h2>
        
        <div className="space-y-6">
          <p className="text-dark-navy/80">
            Match multiple pairs in a row without mistakes to build up an amazing streak! 
            The bigger your streak, the bigger the celebration!
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-orange-100/50 rounded-xl">
              <span className="text-2xl">🔥</span>
              <p className="font-bold text-orange-600 mt-1">2x</p>
              <p className="text-xs text-dark-navy/60">Double Match</p>
            </div>
            <div className="text-center p-4 bg-yellow-100/50 rounded-xl">
              <span className="text-2xl">🌟</span>
              <p className="font-bold text-yellow-600 mt-1">3x</p>
              <p className="text-xs text-dark-navy/60">Triple Streak</p>
            </div>
            <div className="text-center p-4 bg-purple-100/50 rounded-xl">
              <span className="text-2xl">💎</span>
              <p className="font-bold text-purple-600 mt-1">4x</p>
              <p className="text-xs text-dark-navy/60">Quad Streak</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-yellow-100/50 to-orange-100/50 rounded-xl">
              <span className="text-2xl">👑</span>
              <p className="font-bold text-orange-700 mt-1">5x+</p>
              <p className="text-xs text-dark-navy/60">Mega Streak!</p>
            </div>
          </div>
          
          <div className="mt-6">
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Pro Tips</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-medium-teal" />
                <span>Start by revealing shells in a pattern to build a mental map.</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-medium-teal" />
                <span>Focus on one row or area at a time to help remember positions.</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-medium-teal" />
                <span>Take your time! There's no rush, and fewer moves means a better score.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Difficulty Levels */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Difficulty Levels</h2>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-100/50 rounded-xl border-2 border-green-200">
            <h3 className="font-bold text-green-700 mb-2">🌱 Easy</h3>
            <p className="text-sm text-dark-navy/70">12 cards (6 pairs) – Great for young players and beginners!</p>
          </div>
          
          <div className="p-4 bg-yellow-100/50 rounded-xl border-2 border-yellow-200">
            <h3 className="font-bold text-yellow-700 mb-2">🌿 Medium</h3>
            <p className="text-sm text-dark-navy/70">20 cards (10 pairs) – A balanced challenge for everyone.</p>
          </div>
          
          <div className="p-4 bg-red-100/50 rounded-xl border-2 border-red-200">
            <h3 className="font-bold text-red-700 mb-2">🔥 Hard</h3>
            <p className="text-sm text-dark-navy/70">40 cards (20 pairs) – Test your memory to the max!</p>
          </div>
        </div>
      </div>

      {/* Sea Creatures */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Meet the Sea Creatures</h2>
        
        <div className="flex flex-wrap gap-4 justify-center">
          {['🐠', '🐡', '🦈', '🐙', '🐚', '🦀', '🐬', '🐳', '🦑', '🦞', '🦐', '🐢'].map((emoji, i) => (
            <div key={i} className="w-16 h-16 bg-white/70 rounded-xl flex items-center justify-center text-3xl shadow-sm hover:scale-110 transition-transform">
              {emoji}
            </div>
          ))}
        </div>
        <p className="text-center text-dark-navy/60 mt-4 text-sm">
          And many more waiting to be discovered!
        </p>
      </div>

      {/* Bottom CTA */}
      <div className="text-center py-8">
        <Link to="/simple-match/play">
          <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white">
            <Play className="w-5 h-5 mr-2" />
            Start Matching!
          </Button>
        </Link>
      </div>
    </div>
  )
}
